import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useLazyQuery } from "@apollo/client/react";

import { NotificationCreatePage as Component } from "../../../components/pages/notification/NotificationCreatePage";
import {
  CREATE_CS_NOTIFICATION,
  CREATE_CS_NOTIFICATION_IMAGE_UPLOAD_URL,
} from "../../../graphql/mutations";
import { PREVIEW_CS_NOTIFICATION_MARKDOWN } from "../../../graphql/queries";
import { useCheckUserRole } from "../../../hooks/useCheckUserRole";
import { showSnackBar } from "../../../graphql/local-mutations";
import { toISOStringJST } from "../../../utils/date";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const NotificationCreatePage = () => {
  useCheckUserRole();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [medicalInstitutionIds, setMedicalInstitutionIds] = useState<string[]>(
    [],
  );
  const [medicalInstitutionIdInput, setMedicalInstitutionIdInput] =
    useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [pdtCodes, setPdtCodes] = useState<string[]>([]);
  const [startFrom, setStartFrom] = useState("");
  const [endAt, setEndAt] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [createNotification, { loading: isSubmitting }] = useMutation(
    CREATE_CS_NOTIFICATION,
  );
  const [createImageUploadUrl] = useMutation(
    CREATE_CS_NOTIFICATION_IMAGE_UPLOAD_URL,
  );
  const [fetchPreview] = useLazyQuery(PREVIEW_CS_NOTIFICATION_MARKDOWN, {
    fetchPolicy: "network-only",
  });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const handlePreviewTab = useCallback(async () => {
    if (!body) {
      setPreviewHtml("");
      return;
    }
    setIsPreviewLoading(true);
    try {
      const { data } = await fetchPreview({ variables: { body } });
      if (data?.previewCsNotificationMarkdown) {
        setPreviewHtml(data.previewCsNotificationMarkdown);
      }
    } finally {
      setIsPreviewLoading(false);
    }
  }, [body, fetchPreview]);

  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        showSnackBar("ファイルサイズは10MB以下にしてください");
        return;
      }

      setIsUploading(true);
      try {
        const { data } = await createImageUploadUrl({
          variables: {
            input: { fileName: file.name, contentType: file.type },
          },
        });

        const result = data?.createCsNotificationImageUploadUrl;
        if (
          !result ||
          result.__typename === "CsNotificationImageUploadUrlError"
        ) {
          showSnackBar("画像アップロードURLの取得に失敗しました");
          return;
        }

        const { url, fields, key } = result;

        // Presigned POSTでS3に直接アップロード
        const formData = new FormData();
        const parsedFields = JSON.parse(fields);
        for (const [k, v] of Object.entries(parsedFields)) {
          formData.append(k, v as string);
        }
        formData.append("file", file);

        const response = await fetch(url, { method: "POST", body: formData });
        if (!response.ok) {
          showSnackBar("画像のアップロードに失敗しました");
          return;
        }

        // textareaのカーソル位置にMarkdown画像構文を挿入
        // アップロード中にユーザーが編集している可能性があるため、
        // closed-over stateではなくDOMから現在の値を直接読み取る
        const imageMarkdown = `![${file.name}](${key})`;
        const textarea = document.getElementById(
          "notification-body-textarea",
        ) as HTMLTextAreaElement | null;
        if (textarea) {
          const currentValue = textarea.value;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const newBody =
            currentValue.substring(0, start) +
            imageMarkdown +
            currentValue.substring(end);
          setBody(newBody);
        } else {
          setBody((prev) => prev + "\n" + imageMarkdown);
        }
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [createImageUploadUrl],
  );

  const toggleArrayValue = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const handleSubmit = useCallback(async () => {
    try {
      await createNotification({
        variables: {
          input: {
            title,
            body,
            medicalInstitutionIds,
            roles,
            positions,
            pdtCodes,
            startFrom: toISOStringJST(startFrom),
            endAt: endAt ? toISOStringJST(endAt) : undefined,
          },
        },
      });
      showSnackBar("お知らせを投稿しました");
      navigate("/notifications");
    } catch {
      showSnackBar("お知らせの投稿に失敗しました");
    }
  }, [
    title,
    body,
    medicalInstitutionIds,
    roles,
    positions,
    pdtCodes,
    startFrom,
    endAt,
    createNotification,
    navigate,
  ]);

  const handleAddMedicalInstitutionId = useCallback(() => {
    const trimmed = medicalInstitutionIdInput.trim();
    if (trimmed && !medicalInstitutionIds.includes(trimmed)) {
      setMedicalInstitutionIds((prev) => [...prev, trimmed]);
      setMedicalInstitutionIdInput("");
    }
  }, [medicalInstitutionIdInput, medicalInstitutionIds]);

  return (
    <Component
      title={title}
      body={body}
      previewHtml={previewHtml}
      medicalInstitutionIds={medicalInstitutionIds}
      medicalInstitutionIdInput={medicalInstitutionIdInput}
      roles={roles}
      positions={positions}
      pdtCodes={pdtCodes}
      startFrom={startFrom}
      endAt={endAt}
      isSubmitting={isSubmitting}
      isUploading={isUploading}
      isPreviewLoading={isPreviewLoading}
      fileInputRef={fileInputRef}
      onTitleChange={(e) => setTitle(e.target.value)}
      onBodyChange={(e) => setBody(e.target.value)}
      onMedicalInstitutionIdInputChange={(e) =>
        setMedicalInstitutionIdInput(e.target.value)
      }
      onAddMedicalInstitutionId={handleAddMedicalInstitutionId}
      onRemoveMedicalInstitutionId={(id) =>
        setMedicalInstitutionIds((prev) => prev.filter((v) => v !== id))
      }
      onRoleToggle={(role) => setRoles((prev) => toggleArrayValue(prev, role))}
      onPositionToggle={(pos) =>
        setPositions((prev) => toggleArrayValue(prev, pos))
      }
      onPdtCodeToggle={(code) =>
        setPdtCodes((prev) => toggleArrayValue(prev, code))
      }
      onStartFromChange={(e) => setStartFrom(e.target.value)}
      onEndAtChange={(e) => setEndAt(e.target.value)}
      onImageUploadClick={handleImageUploadClick}
      onFileSelect={handleFileSelect}
      onPreviewTab={handlePreviewTab}
      onSubmit={handleSubmit}
      onCancel={() => navigate("/notifications")}
    />
  );
};
